// Ported from dev-scrips-pm-react src/lib/lab-utils.ts (PROD-1119 cross-module closure).
import type {
  BiomarkerResult,
  BiomarkerStatus,
  DomainScore,
  DomainStatus,
  LongevityLabPanel,
} from '@/types/labs'

// ─────────────────────────────────────────────
// STATUS HELPERS
// ─────────────────────────────────────────────

/** Returns true if this status is anything other than optimal */
export function isFlagged(status: BiomarkerStatus): boolean {
  return status !== 'optimal'
}

/** Returns true if status is high/critical */
export function isCritical(status: BiomarkerStatus): boolean {
  return status === 'critical-high' || status === 'critical-low'
}

/** CSS color for a given biomarker status, using design system tokens */
export function statusColor(status: BiomarkerStatus): string {
  switch (status) {
    case 'optimal':
      return 'var(--color-status-success)'
    case 'borderline-low':
    case 'borderline-high':
      return 'var(--color-status-warning)'
    case 'low':
    case 'high':
      return 'var(--color-status-error)'
    case 'critical-low':
    case 'critical-high':
      return 'var(--color-red-600)'
    default:
      return 'var(--color-text-muted)'
  }
}

export function domainStatusColor(status: DomainStatus): string {
  switch (status) {
    case 'optimal':
      return 'var(--color-status-success)'
    case 'needs-attention':
      return 'var(--color-status-warning)'
    case 'critical':
      return 'var(--color-status-error)'
  }
}

// ─────────────────────────────────────────────
// RANGE BAR HELPERS
// Position of the value marker on the range bar (0–100%)
// ─────────────────────────────────────────────

/**
 * Returns the position (0–1) of a value on the optimal range bar,
 * clamped to [0.05, 0.95] for visual clarity.
 * Used to render the marker dot on the range bar.
 */
export function rangeBarPosition(biomarker: BiomarkerResult): number {
  const { value, ranges } = biomarker
  const { low, high } = ranges.optimal

  if (low !== undefined && high !== undefined) {
    // Full range: position relative to [low * 0.5, high * 1.5]
    const visualMin = low * 0.5
    const visualMax = high * 1.5
    return Math.min(0.95, Math.max(0.05, (value - visualMin) / (visualMax - visualMin)))
  }

  if (high !== undefined) {
    // Upper-only range: 0 → high * 2
    return Math.min(0.95, Math.max(0.05, value / (high * 2)))
  }

  if (low !== undefined) {
    // Lower-only range: 0 → low * 2
    return Math.min(0.95, Math.max(0.05, value / (low * 2)))
  }

  return 0.5
}

/**
 * Returns the optimal zone as [left%, width%] for the range bar green zone.
 */
export function rangeBarOptimalZone(biomarker: BiomarkerResult): { left: number; width: number } {
  const { ranges } = biomarker
  const { low, high } = ranges.optimal

  if (low !== undefined && high !== undefined) {
    const visualMin = low * 0.5
    const visualMax = high * 1.5
    const total = visualMax - visualMin
    const left = ((low - visualMin) / total) * 100
    const width = ((high - low) / total) * 100
    return { left, width }
  }

  if (high !== undefined) {
    return { left: 0, width: 50 }   // left half = optimal
  }

  if (low !== undefined) {
    return { left: 50, width: 50 }  // right half = optimal
  }

  return { left: 25, width: 50 }
}

// ─────────────────────────────────────────────
// SPARKLINE PATH GENERATOR
// ─────────────────────────────────────────────

/**
 * Generates an SVG path string from historical trend data.
 * @param points  Array of { value } objects
 * @param width   SVG viewport width
 * @param height  SVG viewport height
 */
export function sparklinePath(
  points: { value: number }[],
  width = 80,
  height = 24
): string {
  if (points.length < 2) return ''

  const values = points.map((p) => p.value)
  const minVal = Math.min(...values)
  const maxVal = Math.max(...values)
  const range = maxVal - minVal || 1

  const xs = points.map((_, i) => (i / (points.length - 1)) * width)
  const ys = values.map((v) => height - ((v - minVal) / range) * height * 0.8 - height * 0.1)

  const d = xs
    .map((x, i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${ys[i].toFixed(1)}`)
    .join(' ')

  return d
}

// ─────────────────────────────────────────────
// DOMAIN SCORING
// ─────────────────────────────────────────────

/**
 * Derives domain scores from a panel's domain sections.
 * Used when a lab doesn't supply composite scores (i.e. all labs except Genova).
 */
export function computeDomainScores(panel: LongevityLabPanel): DomainScore[] {
  return panel.domainSections.map((section) => {
    const flagged = section.biomarkers.filter((b) => isFlagged(b.status))
    const critical = flagged.filter((b) => isCritical(b.status))

    let status: DomainStatus = 'optimal'
    if (critical.length > 0) status = 'critical'
    else if (flagged.length > 0) status = 'needs-attention'

    return {
      domain: section.domain,
      status,
      totalBiomarkers: section.biomarkers.length,
      flaggedBiomarkers: flagged.length,
      criticalFlags: critical.map((b) => b.name),
    }
  })
}

// ─────────────────────────────────────────────
// DISPLAY HELPERS
// ─────────────────────────────────────────────

export function formatValue(biomarker: BiomarkerResult): string {
  if (biomarker.unit === 'elevated flag' || biomarker.unit === 'low flag' || biomarker.unit === 'borderline-low flag') {
    return '↑ Elevated'
  }
  if (biomarker.value === 0 && biomarker.unit === 'low flag') return '↓ Low'

  const decimals = biomarker.value < 10 ? 2 : biomarker.value < 100 ? 1 : 0
  return `${biomarker.value.toFixed(decimals)} ${biomarker.unit}`
}

export function patientAge(dob: string): number {
  const birth = new Date(dob)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

export function sortedDomains(scores: DomainScore[]): DomainScore[] {
  const order: DomainStatus[] = ['critical', 'needs-attention', 'optimal']
  return [...scores].sort(
    (a, b) => order.indexOf(a.status) - order.indexOf(b.status)
  )
}

/** Returns biomarkers sorted: critical → high/low → borderline → optimal */
export function sortedBiomarkers(biomarkers: BiomarkerResult[]): BiomarkerResult[] {
  const order: BiomarkerStatus[] = [
    'critical-high', 'critical-low',
    'high', 'low',
    'borderline-high', 'borderline-low',
    'optimal',
  ]
  return [...biomarkers].sort(
    (a, b) => order.indexOf(a.status) - order.indexOf(b.status)
  )
}

/** Count flagged biomarkers across the whole panel */
export function panelSummary(panel: LongevityLabPanel): {
  total: number
  flagged: number
  critical: number
  optimal: number
} {
  const all = panel.domainSections.flatMap((s) => s.biomarkers)
  return {
    total: all.length,
    flagged: all.filter((b) => isFlagged(b.status)).length,
    critical: all.filter((b) => isCritical(b.status)).length,
    optimal: all.filter((b) => b.status === 'optimal').length,
  }
}

// ─────────────────────────────────────────────
// RATIO GAUGE HELPERS
// ─────────────────────────────────────────────

/**
 * Returns fill percentage (0–100) for the ratio gauge arc.
 * 0% = at lower optimal bound, 100% = 3x above upper optimal bound.
 */
export function ratioGaugeFill(value: number, optimalHigh: number): number {
  const ceiling = optimalHigh * 3
  return Math.min(100, (value / ceiling) * 100)
}

/**
 * Maps a ratio value to a danger level 0–1 for color interpolation.
 * 0 = optimal, 1 = critical
 */
export function ratioDangerLevel(value: number, optimalLow: number, optimalHigh: number): number {
  if (value >= optimalLow && value <= optimalHigh) return 0
  const excess = value > optimalHigh ? (value - optimalHigh) / optimalHigh : 0
  return Math.min(1, excess / 2)
}
